/**
 * Modules a shop can have.
 *
 * The owner of the system decides, per shop, which of these it gets. Everything
 * not listed here - selling, products, receiving stock, the Today screen, staff
 * and settings - every shop always has; a till without them is not a till.
 *
 * A module is switched off by taking its permissions away from everybody in
 * that shop. That one rule does all the work: the server already refuses any
 * call whose permission is missing, and the app already hides any screen,
 * menu entry or button whose permission is missing. So a module that is off is
 * enforced on the server, not merely hidden, and there is no second list of
 * screens to keep in step.
 *
 * Switching a module off deletes nothing. The shop's gift cards, expenses and
 * stock counts stay in its database, and switching the module back on brings
 * them straight back.
 */
import type { Permission } from './permissions.js';

export type FeatureId =
  | 'exchanges'
  | 'giftcards'
  | 'customers'
  | 'suppliers'
  | 'expenses'
  | 'reports'
  | 'stockcount'
  | 'shifts'
  | 'labels'
  | 'promotions';

export interface FeatureMeta {
  id: FeatureId;
  en: string;
  ar: string;
  descEn: string;
  descAr: string;
  /** Taken from everyone in a shop that does not have this module. */
  permissions: Permission[];
}

export const FEATURES: FeatureMeta[] = [
  {
    id: 'exchanges',
    en: 'Exchanges & store credit',
    ar: 'الاستبدال ورصيد المحل',
    descEn: 'Returns and exchanges against a receipt, and store-credit vouchers.',
    descAr: 'الإرجاع والاستبدال بالفاتورة، وقسائم رصيد المحل.',
    permissions: [
      'exchange.do', 'exchange.outside_window', 'exchange.no_receipt', 'exchange.final_sale',
      'exchange.refund_cash', 'credit.view', 'credit.issue',
    ],
  },
  {
    id: 'giftcards',
    en: 'Gift cards',
    ar: 'بطاقات الهدايا',
    descEn: 'Sell gift cards and take them as payment.',
    descAr: 'بيع بطاقات الهدايا وقبولها كدفعة.',
    permissions: ['giftcard.view', 'giftcard.sell'],
  },
  {
    id: 'customers',
    en: 'Customers & tabs',
    ar: 'الزبائن والحسابات',
    descEn: 'A customer list, and selling on account to be paid later.',
    descAr: 'قائمة الزبائن، والبيع على الحساب.',
    permissions: ['customer.view', 'customer.edit', 'customer.tab'],
  },
  {
    id: 'suppliers',
    en: 'Supplier invoices',
    ar: 'فواتير الموردين',
    descEn: 'What is owed to each supplier, and paying it off in instalments.',
    descAr: 'المستحق لكل مورد وتسديده على دفعات.',
    permissions: ['purchase.view', 'purchase.edit'],
  },
  {
    id: 'expenses',
    en: 'Expenses',
    ar: 'المصاريف',
    descEn: 'Rent, wages and bills, with their VAT.',
    descAr: 'الإيجار والرواتب والفواتير، مع ضريبتها.',
    permissions: ['expense.view', 'expense.edit'],
  },
  {
    id: 'reports',
    en: 'Full reports',
    ar: 'التقارير الكاملة',
    descEn: 'Sales, profit, VAT, cash and stock reports, and Excel exports. The Today screen is always there.',
    descAr: 'تقارير المبيعات والربح والضريبة والصندوق والمخزون، والتصدير لإكسل. شاشة اليوم موجودة دائماً.',
    permissions: [
      'reports.sales', 'reports.profit', 'reports.vat', 'reports.cash', 'reports.inventory', 'reports.export',
    ],
  },
  {
    id: 'stockcount',
    en: 'Stock counts',
    ar: 'جرد المخزون',
    descEn: 'Count the shelves with a scanner and correct the stock in one go.',
    descAr: 'جرد الرفوف بالماسح وتصحيح المخزون دفعة واحدة.',
    permissions: ['stock.count', 'stock.count_apply'],
  },
  {
    id: 'shifts',
    en: 'Shifts & Z reports',
    ar: 'الورديات وتقرير Z',
    descEn: 'Count the drawer open and closed, with an end-of-day report.',
    descAr: 'عدّ الصندوق عند الفتح والإغلاق، مع تقرير نهاية اليوم.',
    permissions: ['shift.open', 'shift.close', 'shift.view_all'],
  },
  {
    id: 'labels',
    en: 'Label printing',
    ar: 'طباعة الملصقات',
    descEn: 'Design and print barcode price labels.',
    descAr: 'تصميم وطباعة ملصقات الأسعار بالباركود.',
    permissions: ['labels.print', 'labels.design'],
  },
  {
    id: 'promotions',
    en: 'Promotions',
    ar: 'العروض',
    descEn: 'Percent off and buy-X-get-Y offers that the till applies by itself, with dates.',
    descAr: 'خصم بالنسبة وعروض اشترِ واحصل، بيطبقها الصندوق لحاله، مع تواريخ.',
    permissions: ['promo.manage'],
  },
];

export const ALL_FEATURES: FeatureId[] = FEATURES.map((f) => f.id);

/** Starting points for a new shop. Only the ticked modules are ever stored. */
export const FEATURE_PRESETS: { key: 'basic' | 'standard' | 'full'; en: string; ar: string; features: FeatureId[] }[] = [
  { key: 'basic', en: 'Basic', ar: 'أساسي', features: ['exchanges', 'labels'] },
  {
    key: 'standard',
    en: 'Standard',
    ar: 'قياسي',
    features: ['exchanges', 'labels', 'customers', 'reports', 'stockcount'],
  },
  { key: 'full', en: 'Everything', ar: 'الكل', features: [...ALL_FEATURES] },
];

const OWNER = new Map<Permission, FeatureId>(FEATURES.flatMap((f) => f.permissions.map((p) => [p, f.id] as const)));

/** The module a permission belongs to, or null when every shop has it. */
export function featureOf(p: Permission): FeatureId | null {
  return OWNER.get(p) ?? null;
}

/** Whether a shop with these modules may use this permission at all. */
export function permissionInFeatures(p: Permission, features: readonly string[]): boolean {
  const f = OWNER.get(p);
  return !f || features.includes(f);
}

/** Only real module ids, each once, in catalogue order. Anything else is dropped. */
export function normalizeFeatures(list: unknown): FeatureId[] {
  const wanted = new Set(Array.isArray(list) ? list.map(String) : []);
  return ALL_FEATURES.filter((f) => wanted.has(f));
}
